import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import UnderConstructionDialog from '@/components/UnderConstructionDialog';

const HeroSection = () => {
  const { t, lang, localePath } = useLanguage();
  const Arrow = lang === 'en' ? ArrowRight : ArrowLeft;
  const [aboutText, setAboutText] = useState<string | null>(null);
  const [showConstruction, setShowConstruction] = useState(false);

  useEffect(() => {
    const key = `home_about_${lang}`;
    supabase
      .from('site_settings')
      .select('value')
      .eq('key', key)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        let val: any = data.value;
        if (typeof val === 'string') {
          try { val = JSON.parse(val); } catch {}
        }
        if (typeof val === 'string' && val.trim()) setAboutText(val);
      });
  }, [lang]);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-navy-medium" />
      <div className="absolute top-20 end-0 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px]" />
      <div className="absolute bottom-0 start-0 w-[400px] h-[400px] rounded-full bg-primary/3 blur-[100px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Mobile Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="flex lg:hidden justify-center"
          >
            <div className="relative w-56 h-72 sm:w-64 sm:h-80">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20" />
              <div className="absolute inset-3 rounded-xl overflow-hidden">
                <img
                  src="/images/portrait.avif"
                  alt="אמיר עאסי"
                  className="w-full h-full object-cover"
                  fetchPriority="high"
                  width={224}
                  height={288}
                  decoding="sync"
                />
              </div>
              <div className="absolute -bottom-3 -end-3 w-16 h-16 border-2 border-primary/30 rounded-xl" />
              <div className="absolute -top-3 -start-3 w-10 h-10 border-2 border-primary/20 rounded-xl" />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-start"
          >
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
              <span className="text-gradient-gold">{t('hero.title')}</span>
              <span className="block mt-3 text-xl md:text-2xl lg:text-3xl font-semibold text-foreground/90">
                {t('hero.subtitle')}
              </span>
            </h1>





            <p className="text-base text-muted-foreground/80 max-w-xl mb-10 leading-relaxed">
              {aboutText ?? t('hero.about_text')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 gap-2 text-base">
                <Link to={localePath('/about')}>
                  {t('nav.about')}
                  <Arrow className="w-4 h-4" />
                </Link>
              </Button>
              <Button onClick={() => setShowConstruction(true)} variant="outline" size="lg" className="rounded-full px-8 border-primary/30 hover:bg-primary/10 text-primary text-base">
                {t('hero.cta_lectures')}
              </Button>
            </div>
          </motion.div>

          {/* Desktop Portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative w-[420px] h-[520px]">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20" />
              <div className="absolute inset-4 rounded-xl overflow-hidden">
                <img
                  src="/images/portrait.avif"
                  alt="אמיר עאסי"
                  className="w-full h-full object-cover"
                  fetchPriority="high"
                  width={412}
                  height={512}
                  decoding="sync"
                />
              </div>
              <div className="absolute -bottom-4 -end-4 w-24 h-24 border-2 border-primary/30 rounded-xl" />
              <div className="absolute -top-4 -start-4 w-16 h-16 border-2 border-primary/20 rounded-xl" />
            </div>
          </motion.div>
        </div>
      </div>
      <UnderConstructionDialog open={showConstruction} onOpenChange={setShowConstruction} />
    </section>
  );
};

export default HeroSection;
